import {
  isFrontDesk,
  worksTheDesk,
  deskMay,
  frontDeskMay,
  type DeskAction,
  type FrontDeskItem,
  adjustCreditsResponseSchema,
  importCsvResponseSchema,
  memberProfileSchema,
  packageStartSchema,
  type AdjustSessionsRequest,
  type EndManyPausesRequest,
  type EndManyPausesResponse,
  endManyPausesResponseSchema,
  type FreezeManyRequest,
  type FreezeManyResponse,
  freezeManyResponseSchema,
  type FreezePackageRequest,
  type ExtendPackageRequest,
  type CarryOverPackageRequest,
  type ImportCsvRequest,
  type ImportCsvResponse,
  type MemberProfile,
  type RecordPaymentRequest,
  type PackageStart,
  type SellPackageRequest,
  type CreatePaymentRequest,
  businessSchema,
  type createPackageProductRequestSchema,
  groupSchema,
  creditsResponseSchema,
  enrollmentSchema,
  inviteSchema,
  membershipSchema,
  okSchema,
  packageProductSchema,
  paymentsListResponseSchema,
  paymentProofSchema,
  recurringFeeSchema,
  scheduleTemplateSchema,
  sessionAttendeesResponseSchema,
  type SessionAttendeesResponse,
  type SessionWaitingVisit,
  sessionMutationResultSchema,
  sessionSchema,
  availableSessionSchema,
  type AdjustCreditsResponse,
  type AvailableSession,
  type Business,
  type Group,
  type CreditsResponse,
  type Enrollment,
  type Invite,
  type Membership,
  type MembershipStatus,
  type MyBusiness,
  type PackageProduct,
  type PaymentsListResponse,
  type PaymentProof,
  type PaymentProofType,
  type RecurringFee,
  type ScheduleTemplate,
  type Session,
  type SessionAttendee,
  type SessionMutationResult,
  type UpdatePackageProductRequest,
  type OwnerCancelMembershipRequest,
  type RecordMembershipRenewalRequest,
} from "@teamna/shared";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { z } from "zod";
import { ApiError, api, newIdempotencyKey } from "../api/client.ts";
import { fileToBase64 } from "../lib/base64.ts";
import { businessScheduleQueries, queryKeys } from "./keys.ts";
import { useBusinesses } from "./use-member.ts";

/**
 * The owner data layer.
 *
 * One hook per endpoint, named after it, sitting beside `use-member.ts` rather than inside it: the
 * owner surface is a different role with a different guard, and mixing the two would mean every
 * member screen importing hooks it can never call. Mirrors `apps/mobile/src/hooks/use-owner.ts`
 * endpoint for endpoint, so the two surfaces cannot drift apart.
 *
 * Everything here is scoped to one business. `useActiveBusiness` decides which one, and every hook
 * below takes the id rather than reading the store itself, so a hook stays a hook and the screens
 * stay testable without a store.
 */

const ownerBase = (businessId: string) => `/owner/businesses/${businessId}`;

/** `enabled` for a query that cannot run without a business. Keeps the "none" placeholder honest. */
const withBusiness = (businessId: string | null) => Boolean(businessId);

// --- the active business ----------------------------------------------------

const ACTIVE_KEY = "teamna-web-owner-business";

function readStoredBusinessId(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_KEY);
  } catch {
    // Private browsing. An in-memory selection for this page load is still better than none.
    return null;
  }
}

let activeBusinessId: string | null =
  typeof window === "undefined" ? null : readStoredBusinessId();
const activeListeners = new Set<() => void>();

function readActiveBusinessId(): string | null {
  return activeBusinessId;
}

/**
 * Remembers which business the owner is looking at, across screens and across reloads.
 *
 * Deliberately the same shape as the auth store: a module-level value, a set of listeners, and
 * `useSyncExternalStore`. Two businesses is the realistic ceiling for a pilot owner and the whole thing
 * is one string, so a state library would be more code than the feature.
 */
export function setActiveBusinessId(next: string | null): void {
  if (activeBusinessId === next) return;
  activeBusinessId = next;
  try {
    if (next === null) window.localStorage.removeItem(ACTIVE_KEY);
    else window.localStorage.setItem(ACTIVE_KEY, next);
  } catch {
    // Kept in memory only. See readStoredBusinessId.
  }
  for (const listener of activeListeners) listener();
}

function subscribeActiveBusiness(listener: () => void): () => void {
  activeListeners.add(listener);
  return () => {
    activeListeners.delete(listener);
  };
}

export type ActiveBusiness = {
  /** The business being managed, or null while the list is loading or the account manages none. */
  business: MyBusiness | null;
  /** Every business this account owns, is an admin of or works the front desk of: what the switcher offers. */
  managed: MyBusiness[];
  isLoading: boolean;
  error: unknown;
  select: (businessId: string) => void;
  /** True when the account can open the owner tools at all. */
  canManage: boolean;
  isOwner: boolean;
  /** The Front desk role here (T1): the office shows only the desk part of it. */
  isFrontDesk: boolean;
  /**
   * True when this person may do the job here: the API's own map (`deskMay`), with the business's
   * Front desk setting (`MyBusiness.frontDesk`) for the front desk.
   */
  may: (action: DeskAction) => boolean;
  /**
   * True when this person may use an item of Settings, Front desk here: the owner and admins always,
   * the front desk when the item is on (`frontDeskMay`). For an item that changes a page, not a route.
   */
  mayUse: (item: FrontDeskItem) => boolean;
};

/**
 * Which business the owner tools are pointed at.
 *
 * Falls back to the first managed business when nothing is stored or the stored id no longer belongs to
 * this account, so a stale selection from a previous sign-in can never land the owner on an empty
 * screen for a business they cannot see.
 */
export function useActiveBusiness(): ActiveBusiness {
  const businesses = useBusinesses();
  const stored = useSyncExternalStore(
    subscribeActiveBusiness,
    readActiveBusinessId,
    readActiveBusinessId,
  );

  // Owner, admin and the front desk (the desk part of the office, T1). Staff run sessions; the
  // owner office is not theirs, and the API refuses them almost everything in it.
  const managed = useMemo(
    () => (businesses.data ?? []).filter((row) => worksTheDesk(row.staffRole)),
    [businesses.data],
  );

  const business = useMemo(
    () => managed.find((row) => row.businessId === stored) ?? managed[0] ?? null,
    [managed, stored],
  );

  return {
    business,
    managed,
    isLoading: businesses.isLoading,
    error: businesses.error,
    select: setActiveBusinessId,
    canManage: business !== null,
    isOwner: business?.staffRole === "owner",
    isFrontDesk: isFrontDesk(business?.staffRole),
    may: (action) => deskMay(business?.staffRole, action, business?.frontDesk),
    mayUse: (item) => !isFrontDesk(business?.staffRole) || frontDeskMay(business?.frontDesk, item),
  };
}

// --- business ---------------------------------------------------------------

/** GET /v1/owner/businesses/:id */
export function useOwnerBusiness(businessId: string | null): UseQueryResult<Business> {
  return useQuery({
    queryKey: queryKeys.owner.business(businessId ?? "none"),
    queryFn: () => api.get(`${ownerBase(businessId ?? "")}`, businessSchema),
    enabled: withBusiness(businessId),
    staleTime: 5 * 60 * 1000,
  });
}

/** GET /v1/owner/businesses/:id/invites */
export function useOwnerInvites(businessId: string | null): UseQueryResult<Invite[]> {
  return useQuery({
    queryKey: queryKeys.owner.invites(businessId ?? "none"),
    queryFn: () => api.list(`${ownerBase(businessId ?? "")}/invites`, inviteSchema),
    enabled: withBusiness(businessId),
    staleTime: 5 * 60 * 1000,
  });
}

/** POST /v1/owner/businesses/:id/invites */
export function useCreateInvite(
  businessId: string,
): UseMutationResult<Invite, unknown, { kind?: "member" | "staff" } | void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${ownerBase(businessId)}/invites`, { kind: input?.kind ?? "member" }, inviteSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.invites(businessId) });
    },
  });
}

// --- catalog ----------------------------------------------------------------

/** GET /v1/owner/businesses/:id/groups */
export function useOwnerGroups(businessId: string | null): UseQueryResult<Group[]> {
  return useQuery({
    queryKey: queryKeys.owner.groups(businessId ?? "none"),
    queryFn: () => api.list(`${ownerBase(businessId ?? "")}/groups`, groupSchema),
    enabled: withBusiness(businessId),
    staleTime: 5 * 60 * 1000,
  });
}

/** GET /v1/owner/businesses/:id/schedule-templates */
export function useOwnerScheduleTemplates(
  businessId: string | null,
): UseQueryResult<ScheduleTemplate[]> {
  return useQuery({
    queryKey: queryKeys.owner.templates(businessId ?? "none"),
    queryFn: () =>
      api.list(`${ownerBase(businessId ?? "")}/schedule-templates`, scheduleTemplateSchema),
    enabled: withBusiness(businessId),
    staleTime: 5 * 60 * 1000,
  });
}

/** GET /v1/owner/businesses/:id/package-products (owner only) */
export function useOwnerPackageProducts(
  businessId: string | null,
  enabled = true,
): UseQueryResult<PackageProduct[]> {
  return useQuery({
    queryKey: queryKeys.owner.products(businessId ?? "none"),
    queryFn: () => api.list(`${ownerBase(businessId ?? "")}/package-products`, packageProductSchema),
    enabled: withBusiness(businessId) && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// --- sessions ---------------------------------------------------------------

/**
 * GET /v1/owner/businesses/:id/sessions?from&to
 *
 * Answers the same enriched rows the member availability list uses, so an owner row already carries
 * the group name, its colour, how many are booked and how many spots are left. `from` and `to`
 * are required by the API, which is why they are arguments rather than defaults hidden in here.
 */
export function useOwnerSessions(params: {
  businessId: string | null;
  from: string;
  to: string;
}): UseQueryResult<AvailableSession[]> {
  const query = useQuery({
    queryKey: queryKeys.owner.sessions(params.businessId ?? "none", params.from, params.to),
    queryFn: () =>
      readWithStart(() =>
        api.list(`${ownerBase(params.businessId ?? "")}/sessions`, availableSessionSchema, {
          query: { from: params.from, to: params.to },
        }),
      ),
    enabled: withBusiness(params.businessId),
    staleTime: 30 * 1000,
    // A new list on every read, so each read keeps its own start time (`readWithStart`).
    structuralSharing: false,
  });
  useRefreshAtSessionEnd(query.data);
  return query;
}

/** The longest wait `setTimeout` takes (about 24.8 days). A later end needs no timer yet. */
const MAX_TIMER_MS = 2_147_483_647;

/** When the read of each answer started: the answer shows the server as it was then. */
const readStarts = new WeakMap<object, number>();

function readWithStart<T extends object>(read: () => Promise<T>): Promise<T> {
  const startedAt = Date.now();
  return read().then((data) => {
    readStarts.set(data, startedAt);
    return data;
  });
}

/**
 * Reads the owner's sessions and the open session's people again just after each session ends.
 * Another device (the coach) marks who came during the session, and at the end the screen changes
 * to the ended facts, so it must not keep the counts from before (review F2.1 of session-who-came).
 *
 * The ends count from the start of the read that gave the list, not from now: a read that started
 * before an end and came back after it still gets a fresh read (F4.1). At the end the reads in
 * flight are cancelled first, then read again (F3.1, F4.1). One refresh per end: `refreshedAt` keeps
 * an end that already had its refresh from starting another, and a failed read is retried by the
 * normal query retry, never by a loop here (F3.2).
 */
function useRefreshAtSessionEnd(sessions: AvailableSession[] | undefined): void {
  const queryClient = useQueryClient();
  // The time of the last end refresh.
  const [refreshedAt, setRefreshedAt] = useState(0);
  const readAt = sessions ? readStarts.get(sessions) : undefined;
  const ends = (sessions ?? [])
    .filter((session) => session.status === "scheduled")
    .map((session) => session.endsAt)
    .join(",");
  useEffect(() => {
    const now = Date.now();
    const from = Math.max(readAt ?? now, refreshedAt);
    const next = ends
      .split(",")
      .filter(Boolean)
      .map((endsAt) => new Date(endsAt).getTime())
      .filter((end) => end > from)
      .sort((a, b) => a - b)[0];
    if (next === undefined) return;
    const wait = Math.max(0, next - now + 1000);
    if (wait > MAX_TIMER_MS) return;
    const timer = setTimeout(() => {
      setRefreshedAt(Date.now());
      void refreshAfterEnd(queryClient);
    }, wait);
    return () => clearTimeout(timer);
  }, [ends, readAt, refreshedAt, queryClient]);
}

async function refreshAfterEnd(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  const sessions = { queryKey: queryKeys.owner.sessionsAll };
  const attendees = { queryKey: queryKeys.owner.attendeesAll };
  // A read in flight may have started before the end: cancel it, so the new read replaces it.
  await Promise.all([queryClient.cancelQueries(sessions), queryClient.cancelQueries(attendees)]);
  await Promise.all([queryClient.invalidateQueries(sessions), queryClient.invalidateQueries(attendees)]);
}

/**
 * GET /v1/owner/businesses/:id/sessions/:sessionId/attendees
 *
 * Only fetched once a session sheet is actually open, which is why `sessionId` is nullable and the
 * query is disabled without one. Fetching a week's worth of registers up front to fill a sheet the
 * owner may never open is a lot of rows for a screen that is one tap wide.
 *
 * One read answers two hooks: the bookings (`useOwnerSessionAttendees`) and the names that wait for
 * a parent and came (`useOwnerSessionWaiting`). Same key, so the panel makes one request.
 */
function sessionAttendeesQuery(businessId: string | null, sessionId: string | null) {
  return {
    queryKey: queryKeys.owner.attendees(businessId ?? "none", sessionId ?? "none"),
    queryFn: () => api.get(`${ownerBase(businessId ?? "")}/sessions/${sessionId ?? ""}/attendees`, sessionAttendeesResponseSchema),
    enabled: withBusiness(businessId) && sessionId !== null,
    staleTime: 30 * 1000,
  };
}

export function useOwnerSessionAttendees(
  businessId: string | null,
  sessionId: string | null,
): UseQueryResult<SessionAttendee[]> {
  return useQuery({ ...sessionAttendeesQuery(businessId, sessionId), select: selectAttendees });
}

/** The names that wait for a parent and came to the session (no booking yet). */
export function useOwnerSessionWaiting(businessId: string | null, sessionId: string | null): UseQueryResult<SessionWaitingVisit[]> {
  return useQuery({ ...sessionAttendeesQuery(businessId, sessionId), select: selectWaiting });
}

// Stable functions, so `select` runs only when the data changes.
function selectAttendees(response: SessionAttendeesResponse): SessionAttendee[] {
  return response.items;
}

function selectWaiting(response: SessionAttendeesResponse): SessionWaitingVisit[] {
  return response.waiting ?? [];
}

/** POST /v1/owner/businesses/:id/sessions, the ad-hoc case. */
export function useCreateSession(
  businessId: string,
): UseMutationResult<Session, unknown, { groupId: string; startsAt: string; capacity?: number | null }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${ownerBase(businessId)}/sessions`, input, sessionSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
      void queryClient.invalidateQueries(businessScheduleQueries);
      // A new session can open a usual day on the member home, also for an owner who is a member.
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessionsAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

/**
 * PATCH /v1/owner/businesses/:id/sessions/:sessionId, the move case.
 *
 * Answers a `SessionMutationResult` rather than a bare session, because the fan-out counts are the
 * only thing the owner actually wants confirmed after a change that reaches other people's phones.
 */
export function useMoveSession(
  businessId: string,
): UseMutationResult<SessionMutationResult, unknown, { sessionId: string; startsAt: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.patch(
        `${ownerBase(businessId)}/sessions/${input.sessionId}`,
        { startsAt: input.startsAt },
        sessionMutationResultSchema,
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
      void queryClient.invalidateQueries(businessScheduleQueries);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.attendeesAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

/** POST /v1/owner/businesses/:id/sessions/:sessionId/cancel, the business cancel. */
export function useCancelSession(
  businessId: string,
): UseMutationResult<SessionMutationResult, unknown, { sessionId: string; reason: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(
        `${ownerBase(businessId)}/sessions/${input.sessionId}/cancel`,
        { reason: input.reason },
        sessionMutationResultSchema,
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
      void queryClient.invalidateQueries(businessScheduleQueries);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.attendeesAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.credits });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

/**
 * The request as the client sends it. `kind` and `autoRenew` have server defaults, so the form may
 * leave them out.
 */
type CreatePackageProductInput = z.input<typeof createPackageProductRequestSchema>;

/**
 * POST /v1/owner/businesses/:id/package-products
 *
 * Owner only, enforced by the API rather than by hiding the button: a price is the one thing in the
 * product a staff account must not be able to change.
 */
export function useCreatePackageProduct(
  businessId: string,
): UseMutationResult<PackageProduct, unknown, CreatePackageProductInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${ownerBase(businessId)}/package-products`, input, packageProductSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.products(businessId) });
      // The member-facing list is a different key on a different root, and a package that has just
      // gone on sale should show up there without a reload.
      void queryClient.invalidateQueries({ queryKey: queryKeys.packages(businessId) });
    },
  });
}

/** PATCH /v1/owner/businesses/:id/package-products/:id, which also hides and restores one. */
export function useUpdatePackageProduct(
  businessId: string,
): UseMutationResult<
  PackageProduct,
  unknown,
  { productId: string; patch: UpdatePackageProductRequest }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, patch }) =>
      api.patch(
        `${ownerBase(businessId)}/package-products/${productId}`,
        patch,
        packageProductSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.products(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.packages(businessId) });
    },
  });
}

// --- members, enrollments, credits ------------------------------------------

/** GET /v1/owner/businesses/:id/members */
export function useOwnerMembers(businessId: string | null): UseQueryResult<Membership[]> {
  return useQuery({
    queryKey: queryKeys.owner.members(businessId ?? "none"),
    queryFn: () => api.list(`${ownerBase(businessId ?? "")}/members`, membershipSchema),
    enabled: withBusiness(businessId),
  });
}

/**
 * PATCH /v1/owner/businesses/:id/members/:membershipId
 *
 * Answers `{ ok: true }`, not the updated row, so the list is refetched rather than patched in
 * place. Worth knowing before writing an optimistic update against a body that is not there.
 */
export function useUpdateMembership(
  businessId: string,
): UseMutationResult<
  { ok: true },
  unknown,
  { membershipId: string; status: MembershipStatus }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.patch(
        `${ownerBase(businessId)}/members/${input.membershipId}`,
        { status: input.status },
        okSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
    },
  });
}

/** GET /v1/owner/businesses/:id/enrollments */
export function useOwnerEnrollments(businessId: string | null): UseQueryResult<Enrollment[]> {
  return useQuery({
    queryKey: queryKeys.owner.enrollments(businessId ?? "none"),
    queryFn: () => api.list(`${ownerBase(businessId ?? "")}/enrollments`, enrollmentSchema),
    enabled: withBusiness(businessId),
  });
}

/** PATCH /v1/owner/businesses/:id/enrollments/:enrollmentId */
export function useUpdateEnrollment(
  businessId: string,
): UseMutationResult<
  Enrollment,
  unknown,
  { enrollmentId: string; status: Enrollment["status"] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.patch(
        `${ownerBase(businessId)}/enrollments/${input.enrollmentId}`,
        { status: input.status },
        enrollmentSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.enrollments(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
      void queryClient.invalidateQueries(businessScheduleQueries);
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

/**
 * GET /v1/owner/businesses/:id/members/:membershipId/credits
 *
 * The owner route, not `/me/credits`. It answers the same shape scoped to this business, so the owner
 * deciding whether to hand a credit back is reading exactly what the member sees rather than a
 * second report that could disagree with it.
 */
export function useOwnerMemberCredits(
  businessId: string | null,
  membershipId: string | null,
): UseQueryResult<CreditsResponse> {
  return useQuery({
    queryKey: queryKeys.owner.memberCredits(businessId ?? "none", membershipId ?? "none"),
    queryFn: () =>
      api.get(
        `${ownerBase(businessId ?? "")}/members/${membershipId ?? ""}/credits`,
        creditsResponseSchema,
      ),
    enabled: withBusiness(businessId) && Boolean(membershipId),
    staleTime: 30 * 1000,
  });
}

/**
 * POST /v1/owner/businesses/:id/credits/adjust, the owner's goodwill override.
 *
 * The adjustment hangs off a purchase rather than a member, because credits expire per purchase.
 * Every one is audit-logged with its note, which is why the note is required by the contract.
 */
export function useAdjustCredits(
  businessId: string,
): UseMutationResult<
  AdjustCreditsResponse,
  unknown,
  { purchaseId: string; delta: number; note: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${ownerBase(businessId)}/credits/adjust`, input, adjustCreditsResponseSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.memberCreditsAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.credits });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

// --- money ------------------------------------------------------------------

/** GET /v1/owner/businesses/:id/payments (owner only) */
export function useOwnerPayments(
  businessId: string | null,
  enabled = true,
): UseQueryResult<PaymentsListResponse> {
  return useQuery({
    queryKey: queryKeys.owner.payments(businessId ?? "none"),
    queryFn: () => api.get(`${ownerBase(businessId ?? "")}/payments`, paymentsListResponseSchema),
    enabled: withBusiness(businessId) && enabled,
    staleTime: 60 * 1000,
    refetchInterval: (query) => paymentsRefetchMs(query.state.data),
  });
}

/**
 * A proof of payment opens through a signed URL that lives one to two hours: while the page stays
 * open with a proof on it, the list is read again before the URL expires. A hidden tab reads it again
 * on focus.
 */
export function paymentsRefetchMs(data: PaymentsListResponse | undefined): number | false {
  return data?.items.some((item) => item.proof?.url) ? 45 * 60 * 1000 : false;
}

/**
 * GET /v1/owner/businesses/:id/recurring-fees (owner only)
 *
 * The swimming pilot's whole revenue model: a monthly fee per weekly slot. A fee hangs off an
 * enrollment rather than a member, so the name on a row comes from the enrollments list.
 */
export function useOwnerRecurringFees(
  businessId: string | null,
  enabled = true,
): UseQueryResult<RecurringFee[]> {
  return useQuery({
    queryKey: queryKeys.owner.recurringFees(businessId ?? "none"),
    queryFn: () => api.list(`${ownerBase(businessId ?? "")}/recurring-fees`, recurringFeeSchema),
    enabled: withBusiness(businessId) && enabled,
    staleTime: 60 * 1000,
  });
}

/** PATCH /v1/owner/businesses/:id/recurring-fees/:feeId, to stop one or start it again. */
export function useUpdateRecurringFee(
  businessId: string,
): UseMutationResult<RecurringFee, unknown, { feeId: string; active: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.patch(
        `${ownerBase(businessId)}/recurring-fees/${input.feeId}`,
        { active: input.active },
        recurringFeeSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.recurringFees(businessId) });
    },
  });
}

// --- member profile and CSV import (slice D) ---------------------------------

/**
 * GET /v1/business/businesses/:id/participants/:participantId
 *
 * The profile the member page shows per participant: balances, upcoming bookings with the engine's
 * cancel sentence, and the ledger with the reason on every line.
 */
export function useParticipantProfile(
  businessId: string | null,
  participantId: string | null,
): UseQueryResult<MemberProfile> {
  return useQuery({
    queryKey: queryKeys.owner.participantProfile(businessId ?? "none", participantId ?? "none"),
    queryFn: () =>
      api.get(
        `/business/businesses/${businessId ?? ""}/participants/${participantId ?? ""}`,
        memberProfileSchema,
      ),
    enabled: withBusiness(businessId) && Boolean(participantId),
    staleTime: 30 * 1000,
  });
}

/** Refreshes what a balance change touches: the profile, the members table and the balances step. */
function refreshBalances(queryClient: ReturnType<typeof useQueryClient>, businessId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.participantProfileAll });
  void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.memberCreditsAll });
  // An owner can be a member too: their own agenda, balance and member home read the same rows.
  void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
  void queryClient.invalidateQueries({ queryKey: queryKeys.credits });
  void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
}

/** POST /v1/business/businesses/:id/participants/:participantId/adjust, with a reason the member reads. */
export function useAdjustSessions(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, AdjustSessionsRequest & { participantId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, ...body }) =>
      api.post(
        `/business/businesses/${businessId}/participants/${participantId}/adjust`,
        body,
        memberProfileSchema,
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: (profile) => {
      queryClient.setQueryData(
        queryKeys.owner.participantProfile(businessId, profile.participant.id),
        profile,
      );
      refreshBalances(queryClient, businessId);
    },
  });
}

/**
 * POST /v1/business/businesses/:id/participants/:participantId/freezes: pause a package of the
 * family (migration 0071). The end date moves by the paused days.
 */
export function useFreezePackage(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, FreezePackageRequest & { participantId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, ...body }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/freezes`, body, memberProfileSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.owner.participantProfile(businessId, profile.participant.id), profile);
      refreshBalances(queryClient, businessId);
    },
  });
}

/**
 * The idempotency keys of the Extend and Carry over requests whose outcome is not known yet (no
 * answer, a 5xx, or the first try still runs), by action, participant and body. A retry of the same
 * request, also after the panel closed and opened again, sends the same key and gets the saved
 * answer, not a conflict (review F3.1, F4.1). A known outcome (an answer, or another 4xx) drops it.
 */
const unresolvedKeys = new Map<string, string>();

/** The key for this request: the unresolved one when there is one, else a new one. */
export function requestKey(request: string): string {
  let key = unresolvedKeys.get(request);
  if (!key) {
    key = newIdempotencyKey();
    unresolvedKeys.set(request, key);
  }
  return key;
}

/**
 * The outcome of the request is known: a retry is a new request. Not for `IDEMPOTENCY_IN_PROGRESS`
 * (409): the first try with this key still runs on the server, and a retry with the same key gets
 * its answer (review F5.1).
 */
export function settleRequestKey(request: string, error: unknown): void {
  const known = error === null || (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.code !== "IDEMPOTENCY_IN_PROGRESS");
  if (known) unresolvedKeys.delete(request);
}

/**
 * A POST on one participant of the business that answers with their profile (Extend, Carry over):
 * the profile goes into the cache, and the balances read again. Keys by `requestKey`.
 */
function useParticipantProfileAction<Body extends object>(
  businessId: string,
  action: string,
): UseMutationResult<MemberProfile, unknown, Body & { participantId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ participantId, ...body }) => {
      const request = JSON.stringify([businessId, action, participantId, body]);
      try {
        const profile = await api.post(`/business/businesses/${businessId}/participants/${participantId}/${action}`, body, memberProfileSchema, {
          idempotencyKey: requestKey(request),
        });
        settleRequestKey(request, null);
        return profile;
      } catch (error) {
        settleRequestKey(request, error);
        throw error;
      }
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.owner.participantProfile(businessId, profile.participant.id), profile);
      refreshBalances(queryClient, businessId);
    },
  });
}

/** POST .../participants/:participantId/extend: move the last day of one package to a later day (card #1). */
export function useExtendPackage(businessId: string): UseMutationResult<MemberProfile, unknown, ExtendPackageRequest & { participantId: string }> {
  return useParticipantProfileAction<ExtendPackageRequest>(businessId, "extend");
}

/** POST .../participants/:participantId/carry-over: move the unused sessions of one package to the next one (card #1). */
export function useCarryOverPackage(businessId: string): UseMutationResult<MemberProfile, unknown, CarryOverPackageRequest & { participantId: string }> {
  return useParticipantProfileAction<CarryOverPackageRequest>(businessId, "carry-over");
}

/** POST .../participants/:participantId/freezes/:freezeId/end: end a pause today. */
export function useEndPackageFreeze(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, { participantId: string; freezeId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, freezeId }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/freezes/${freezeId}/end`, {}, memberProfileSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.owner.participantProfile(businessId, profile.participant.id), profile);
      refreshBalances(queryClient, businessId);
    },
  });
}

/**
 * POST .../participants/:participantId/freeze-many: Freeze all (D17). `scope: "all"` pauses every
 * pack of the account (the client, or every child of a family); `"participant"` the packs bought for
 * this child. The answer says how many were paused and which were skipped, and why.
 */
export function useFreezeMany(
  businessId: string,
): UseMutationResult<FreezeManyResponse, unknown, FreezeManyRequest & { participantId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, ...body }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/freeze-many`, body, freezeManyResponseSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.owner.participantProfile(businessId, result.profile.participant.id), result.profile);
      refreshBalances(queryClient, businessId);
    },
  });
}

/** POST .../participants/:participantId/freeze-many/end: end every pause in the scope today (D17). */
export function useEndManyPauses(
  businessId: string,
): UseMutationResult<EndManyPausesResponse, unknown, EndManyPausesRequest & { participantId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, ...body }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/freeze-many/end`, body, endManyPausesResponseSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.owner.participantProfile(businessId, result.profile.participant.id), result.profile);
      refreshBalances(queryClient, businessId);
    },
  });
}

/**
 * POST /v1/business/businesses/:id/participants/:participantId/sell-package. The caller owns the
 * idempotency key: the panel makes one when it opens, so a second press or a resend after a lost
 * answer replays the first sale instead of selling twice.
 */
export function useSellPackage(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, SellPackageRequest & { participantId: string; idempotencyKey: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, idempotencyKey, ...body }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/sell-package`, body, memberProfileSchema, {
        idempotencyKey,
      }),
    onSuccess: (profile) => afterMoney(queryClient, businessId, profile),
  });
}

/** GET /v1/business/businesses/:id/participants/:participantId/package-start: the days a package sold now would run. */
export function usePackageStart(businessId: string, participantId: string, productId: string | null): UseQueryResult<PackageStart> {
  return useQuery({
    queryKey: queryKeys.owner.packageStart(businessId, participantId, productId ?? "none"),
    queryFn: () =>
      api.get(`/business/businesses/${businessId}/participants/${participantId}/package-start?productId=${encodeURIComponent(productId ?? "")}`, packageStartSchema),
    enabled: productId !== null,
  });
}

/** POST /v1/business/businesses/:id/participants/:participantId/record-payment, for an owed package. Same key rule as `useSellPackage`. */
export function useRecordPayment(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, RecordPaymentRequest & { participantId: string; idempotencyKey: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, idempotencyKey, ...body }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/record-payment`, body, memberProfileSchema, {
        idempotencyKey,
      }),
    onSuccess: (profile) => afterMoney(queryClient, businessId, profile),
  });
}

/** The types a proof of payment can be, as the server checks them (migration 0084). */
export const PAYMENT_PROOF_ACCEPT = "image/jpeg,image/png,application/pdf";

/**
 * POST /v1/business/businesses/:id/payment-proofs: one proof of payment, uploaded before the
 * payment. The call that records the payment, or `useSetPaymentProof`, names it by its id.
 */
export function useUploadPaymentProof(businessId: string): UseMutationResult<PaymentProof, unknown, File> {
  return useMutation({
    mutationFn: async (file) =>
      api.post(
        `/business/businesses/${businessId}/payment-proofs`,
        { fileName: file.name, contentType: file.type as PaymentProofType, data: await fileToBase64(file) },
        paymentProofSchema,
        { timeoutMs: 60_000 },
      ),
  });
}

/** PUT /v1/owner/businesses/:id/payments/:paymentId/proof: an uploaded file becomes the proof (owner and admin). */
export function useSetPaymentProof(businessId: string): UseMutationResult<PaymentProof, unknown, { paymentId: string; proofId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, proofId }) => api.put(`${ownerBase(businessId)}/payments/${paymentId}/proof`, { proofId }, paymentProofSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.owner.payments(businessId) }),
  });
}

/** DELETE /v1/owner/businesses/:id/payments/:paymentId/proof (owner and admin). */
export function useRemovePaymentProof(businessId: string): UseMutationResult<void, unknown, { paymentId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId }) => {
      await api.delete(`${ownerBase(businessId)}/payments/${paymentId}/proof`);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.owner.payments(businessId) }),
  });
}

/**
 * POST /v1/business/businesses/:id/participants/:participantId/membership/cancel (M2): `period_end`
 * stops the renewal and the membership ends with the paid month; `now` ends it at once.
 */
export function useOwnerCancelMembership(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, OwnerCancelMembershipRequest & { participantId: string }> {
  return useMutation(ownerCancelMembershipOptions(useQueryClient(), businessId));
}

/** The mutation of `useOwnerCancelMembership`, apart so a test can run its error path. */
export function ownerCancelMembershipOptions(
  queryClient: QueryClient,
  businessId: string,
): UseMutationOptions<MemberProfile, unknown, OwnerCancelMembershipRequest & { participantId: string }> {
  return {
    mutationFn: ({ participantId, ...body }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/membership/cancel`, body, memberProfileSchema),
    onSuccess: (profile) => afterMoney(queryClient, businessId, profile),
    // The cancel can commit and the profile read after it fail; read the client again so the page
    // shows a membership that ended, and the owner does not try again (review F9.2). The promise is
    // returned: the mutation stays pending, and the button locked, until that read ends (F12.1). If
    // that read fails too, a second tap sends the same `membershipId` and gets the profile (F13.1).
    onError: (_error, { participantId }) => queryClient.invalidateQueries({ queryKey: queryKeys.owner.participantProfile(businessId, participantId) }),
  };
}

/** POST .../membership/renewal/record (M2): the next month of a membership, paid in cash or by transfer. Same key rule as `useSellPackage`. */
export function useRecordMembershipRenewal(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, RecordMembershipRenewalRequest & { participantId: string; idempotencyKey: string }> {
  return useMutation(recordMembershipRenewalOptions(useQueryClient(), businessId));
}

/** The mutation of `useRecordMembershipRenewal`, apart so a test can run its error path. */
export function recordMembershipRenewalOptions(
  queryClient: QueryClient,
  businessId: string,
): UseMutationOptions<MemberProfile, unknown, RecordMembershipRenewalRequest & { participantId: string; idempotencyKey: string }> {
  return {
    mutationFn: ({ participantId, idempotencyKey, ...body }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/membership/renewal/record`, body, memberProfileSchema, {
        idempotencyKey,
      }),
    onSuccess: (profile) => afterMoney(queryClient, businessId, profile),
    // A refusal can mean the month on the screen was paid elsewhere and the next one opened: read
    // the client again, so the panel closes and the owner sees the month that waits (review F9.1).
    onError: (_error, { participantId }) => queryClient.invalidateQueries({ queryKey: queryKeys.owner.participantProfile(businessId, participantId) }),
  };
}

/**
 * POST /v1/business/businesses/:id/participants/:participantId/payment-requests: ask the family for an
 * amount. The profile that comes back lists the request with its payment link. Same key rule as
 * `useSellPackage`.
 */
export function useCreatePaymentRequest(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, CreatePaymentRequest & { participantId: string; idempotencyKey: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, idempotencyKey, ...body }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/payment-requests`, body, memberProfileSchema, {
        idempotencyKey,
      }),
    onSuccess: (profile) => afterMoney(queryClient, businessId, profile),
  });
}

/** POST .../participants/:participantId/payment-requests/:requestId/cancel. The link closes. */
export function useCancelPaymentRequest(
  businessId: string,
): UseMutationResult<MemberProfile, unknown, { participantId: string; requestId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, requestId }) =>
      api.post(`/business/businesses/${businessId}/participants/${participantId}/payment-requests/${requestId}/cancel`, {}, memberProfileSchema),
    onSuccess: (profile) => afterMoney(queryClient, businessId, profile),
  });
}

function afterMoney(queryClient: ReturnType<typeof useQueryClient>, businessId: string, profile: MemberProfile): void {
  queryClient.setQueryData(queryKeys.owner.participantProfile(businessId, profile.participant.id), profile);
  refreshBalances(queryClient, businessId);
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.payments(businessId) });
}

const businessCancelResponseSchema = z.object({
  bookingId: z.string().uuid(),
  sessionsReturned: z.number().int(),
});

/** POST /v1/business/businesses/:id/bookings/:bookingId/cancel. Always returns the session. */
export function useBusinessCancelBooking(
  businessId: string,
): UseMutationResult<z.infer<typeof businessCancelResponseSchema>, unknown, { bookingId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId }) =>
      api.post(
        `/business/businesses/${businessId}/bookings/${bookingId}/cancel`,
        undefined,
        businessCancelResponseSchema,
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: () => {
      refreshBalances(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
      void queryClient.invalidateQueries(businessScheduleQueries);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.attendeesAll });
    },
  });
}

/** POST /v1/business/businesses/:id/import/csv. The rows are read from the file in the browser. */
export function useImportCsv(
  businessId: string,
): UseMutationResult<ImportCsvResponse, unknown, ImportCsvRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`/business/businesses/${businessId}/import/csv`, input, importCsvResponseSchema, {
        timeoutMs: 60_000,
      }),
    onSuccess: () => {
      refreshBalances(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
    },
  });
}
